import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import {
  Store,
  CreditCard,
  DollarSign,
  TrendingUp,
  Receipt,
} from 'lucide-react';
import { sortReportData } from './lib/ordering';

export default function PosReports({ data }) {
  const formatCurrency = (amount) => {
    const normalized = Object.is(amount, -0) ? 0 : amount;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(normalized);
  };

  const formatDateTime = (dateString) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(new Date(dateString));
  };

  // Apply global sorting to all data
  const sortedData = sortReportData(data);

  // Filter POS-only data - use the richer sourceBreakdown data if available
  const posBreakdown = data.sourceBreakdown?.find(
    (source) => source._id === 'pos'
  );
  const posData = sortedData.moneyReport.salesBySource.find(
    (source) => source._id === 'pos'
  );

  // Use sourceBreakdown data if available, otherwise fall back to salesBySource
  const posOrders = posBreakdown?.orderCount || posData?.count || 0;
  const posRevenue = posBreakdown?.total || posData?.total || 0;
  const posTax = posBreakdown?.tax || posData?.tax || 0;
  const posSubTotal = posBreakdown?.subTotal || 0;
  const posDeliveryFee = posBreakdown?.deliveryFee || 0;
  const posDiscount = posBreakdown?.discount || 0;
  const posRefundAmount = posBreakdown?.refundAmount || 0;
  const posRewardAmount = posBreakdown?.rewardAmount || 0;
  const posAvgOrderValue =
    posBreakdown?.averageOrderValue ||
    (posOrders > 0 ? posRevenue / posOrders : 0);

  // Order type breakdowns (available in sourceBreakdown)
  const posDeliveryOrders = posBreakdown?.deliveryOrders || 0;
  const posPickupOrders = posBreakdown?.pickupOrders || 0;
  const posDineInOrders = posBreakdown?.dineInOrders || posOrders; // fallback to total orders
  const posDeliveryOrdersWithTip = posBreakdown?.deliveryOrdersWithTip || 0;
  const posPickupOrdersWithTip = posBreakdown?.pickupOrdersWithTip || 0;
  const posDineInOrdersWithTip = posBreakdown?.dineInOrdersWithTip || 0;

  // Tip data from sourceBreakdown (more accurate)
  const posTotalTips = posBreakdown?.tip || posData?.totalTip || 0;
  const posKitchenTips = posBreakdown?.kitchenTip || 0;
  const posDriverTips = posBreakdown?.driverTip || 0;
  const posOrdersWithTip =
    posBreakdown?.orderCountWithTip ||
    posBreakdown?.countWithTip ||
    posData?.countWithTip ||
    0;
  const posAvgTipPerOrder =
    posOrdersWithTip > 0 ? posTotalTips / posOrdersWithTip : 0;

  return (
    <div className="space-y-8">
      {/* Clover Style Report */}
      <Card className="print-card">
        <CardHeader className="bg-green-50 dark:bg-green-900/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-green-600 p-2 rounded-lg">
                <Store className="text-white h-6 w-6" />
              </div>
              <div>
                <CardTitle className="text-green-800 dark:text-green-200 text-xl">
                  Clover Style POS Report
                </CardTitle>
                <p className="text-green-600 dark:text-green-400 text-sm">
                  In-Store Transactions Only
                </p>
              </div>
            </div>
            <Badge
              variant="secondary"
              className="bg-green-100 text-green-800 border-green-200"
            >
              POS Terminal
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-8">
            <div className="bg-white dark:bg-gray-800 border border-green-200 dark:border-green-700 rounded-lg p-4 text-center">
              <div className="text-3xl font-bold text-green-600 mb-1">
                {posOrders.toLocaleString()}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Total Orders
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 border border-green-200 dark:border-green-700 rounded-lg p-4 text-center">
              <div className="text-3xl font-bold text-green-600 mb-1">
                {formatCurrency(posRevenue)}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Gross Sales
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 border border-green-200 dark:border-green-700 rounded-lg p-4 text-center">
              <div className="text-3xl font-bold text-green-600 mb-1">
                {formatCurrency(posTax)}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Tax Collected
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 border border-green-200 dark:border-green-700 rounded-lg p-4 text-center">
              <div className="text-3xl font-bold text-green-600 mb-1">
                {formatCurrency(posTotalTips)}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Total Tips
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 border border-green-200 dark:border-green-700 rounded-lg p-4 text-center">
              <div className="text-3xl font-bold text-green-600 mb-1">
                {formatCurrency(posAvgOrderValue)}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Avg Order
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 border border-green-200 dark:border-green-700 rounded-lg p-4 text-center">
              <div className="text-3xl font-bold text-green-600 mb-1">
                {posOrdersWithTip.toLocaleString()}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Orders with Tips
              </div>
            </div>
          </div>

          {/* Order Type Breakdown */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4 flex items-center">
              <Store className="mr-2 h-5 w-5 text-green-600" />
              Order Type Breakdown
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                  Pickup Orders
                </div>
                <div className="text-2xl font-semibold text-green-600">
                  {posPickupOrders.toLocaleString()}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {posPickupOrdersWithTip} with tips (
                  {posPickupOrders > 0
                    ? (
                        (posPickupOrdersWithTip / posPickupOrders) *
                        100
                      ).toFixed(1)
                    : '0'}
                  %)
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                  Delivery Orders
                </div>
                <div className="text-2xl font-semibold text-green-600">
                  {posDeliveryOrders.toLocaleString()}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {posDeliveryOrdersWithTip} with tips (
                  {posDeliveryOrders > 0
                    ? (
                        (posDeliveryOrdersWithTip / posDeliveryOrders) *
                        100
                      ).toFixed(1)
                    : '0'}
                  %)
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                  Dine-In Orders
                </div>
                <div className="text-2xl font-semibold text-green-600">
                  {posDineInOrders.toLocaleString()}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {posDineInOrdersWithTip} with tips (
                  {posDineInOrders > 0
                    ? (
                        (posDineInOrdersWithTip / posDineInOrders) *
                        100
                      ).toFixed(1)
                    : '0'}
                  %)
                </div>
              </div>
            </div>
          </div>

          {/* POS Tips Breakdown */}
          {posTotalTips > 0 && (
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4 flex items-center">
                <DollarSign className="mr-2 h-5 w-5 text-green-600" />
                POS Tips Breakdown
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                    Kitchen Tips
                  </div>
                  <div className="text-2xl font-semibold text-green-600">
                    {formatCurrency(posKitchenTips)}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Avg:{' '}
                    {formatCurrency(
                      posOrdersWithTip > 0
                        ? posKitchenTips / posOrdersWithTip
                        : 0
                    )}
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                    Driver Tips
                  </div>
                  <div className="text-2xl font-semibold text-green-600">
                    {formatCurrency(posDriverTips)}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Avg:{' '}
                    {formatCurrency(
                      posOrdersWithTip > 0
                        ? posDriverTips / posOrdersWithTip
                        : 0
                    )}
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                    Avg Tip per Order
                  </div>
                  <div className="text-2xl font-semibold text-green-600">
                    {formatCurrency(posAvgTipPerOrder)}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {posOrdersWithTip} orders with tips
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Payment Methods for POS */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4 flex items-center">
              <CreditCard className="mr-2 h-5 w-5 text-green-600" />
              Payment Methods
            </h3>
            <div className="space-y-3">
              {sortedData.moneyReport.tenderTypes.map((tender, index) => (
                <div
                  key={index}
                  className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
                >
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                    <span className="font-medium capitalize text-gray-800 dark:text-gray-200">
                      {tender._id === 'other' ? 'Other' : tender._id}
                    </span>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-gray-800 dark:text-gray-200">
                      {formatCurrency(tender.totalAmount)}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {tender.count} transactions
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Square Style Report */}
      <Card className="print-card">
        <CardHeader className="bg-blue-50 dark:bg-blue-900/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-600 p-2 rounded-lg">
                <Receipt className="text-white h-6 w-6" />
              </div>
              <div>
                <CardTitle className="text-blue-800 dark:text-blue-200 text-xl">
                  Square Style POS Report
                </CardTitle>
                <p className="text-blue-600 dark:text-blue-400 text-sm">
                  Point of Sale Summary
                </p>
              </div>
            </div>
            <Badge
              variant="secondary"
              className="bg-blue-100 text-blue-800 border-blue-200"
            >
              Square POS
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {/* Key Metrics */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/10 dark:to-indigo-900/10 rounded-lg p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <div className="flex items-center mb-3">
                  <DollarSign className="h-5 w-5 text-blue-600 mr-2" />
                  <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-200">
                    Sales Performance
                  </h3>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">
                      Gross Sales:
                    </span>
                    <span className="font-semibold text-gray-800 dark:text-gray-200">
                      {formatCurrency(posRevenue)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">
                      Tax:
                    </span>
                    <span className="font-semibold text-gray-800 dark:text-gray-200">
                      {formatCurrency(posTax)}
                    </span>
                  </div>
                  <div className="flex justify-between border-t border-gray-200 dark:border-gray-700 pt-2">
                    <span className="text-gray-800 dark:text-gray-200 font-medium">
                      Net Sales:
                    </span>
                    <span className="font-bold text-blue-600">
                      {formatCurrency(posRevenue - posTax)}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center mb-3">
                  <TrendingUp className="h-5 w-5 text-blue-600 mr-2" />
                  <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-200">
                    Transaction Metrics
                  </h3>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">
                      Total Transactions:
                    </span>
                    <span className="font-semibold text-gray-800 dark:text-gray-200">
                      {posOrders.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">
                      Average Sale:
                    </span>
                    <span className="font-semibold text-gray-800 dark:text-gray-200">
                      {posOrders > 0
                        ? formatCurrency(posRevenue / posOrders)
                        : '$0.00'}
                    </span>
                  </div>
                  <div className="flex justify-between border-t border-gray-200 dark:border-gray-700 pt-2">
                    <span className="text-gray-800 dark:text-gray-200 font-medium">
                      Tax Rate:
                    </span>
                    <span className="font-bold text-blue-600">
                      {posRevenue > 0
                        ? ((posTax / posRevenue) * 100).toFixed(2) + '%'
                        : '0%'}
                    </span>
                  </div>
                </div>
              </div>

              {/* POS Tips Performance */}
              {posTotalTips > 0 && (
                <div>
                  <div className="flex items-center mb-3">
                    <DollarSign className="h-5 w-5 text-blue-600 mr-2" />
                    <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-200">
                      Tips Performance
                    </h3>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">
                        Total Tips:
                      </span>
                      <span className="font-semibold text-gray-800 dark:text-gray-200">
                        {formatCurrency(posTotalTips)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">
                        Avg Tip:
                      </span>
                      <span className="font-semibold text-gray-800 dark:text-gray-200">
                        {formatCurrency(posAvgTipPerOrder)}
                      </span>
                    </div>
                    <div className="flex justify-between border-t border-gray-200 dark:border-gray-700 pt-2">
                      <span className="text-gray-800 dark:text-gray-200 font-medium">
                        Tips % of Sales:
                      </span>
                      <span className="font-bold text-blue-600">
                        {posRevenue > 0
                          ? ((posTotalTips / posRevenue) * 100).toFixed(2)
                          : '0.00'}
                        %
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Payment Breakdown */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4 flex items-center">
              <CreditCard className="mr-2 h-5 w-5 text-blue-600" />
              Payment Breakdown
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {sortedData.moneyReport.tenderTypes.map((tender, index) => {
                const percentage =
                  sortedData.moneyReport.summary.totalAmount > 0
                    ? (
                        (tender.totalAmount /
                          sortedData.moneyReport.summary.totalAmount) *
                        100
                      ).toFixed(1)
                    : '0.0';

                return (
                  <div
                    key={index}
                    className="bg-white dark:bg-gray-800 border border-blue-200 dark:border-blue-700 rounded-lg p-4"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="font-medium text-gray-800 dark:text-gray-200 capitalize">
                          {tender._id === 'other' ? 'Other' : tender._id}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {tender.count} transactions
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-blue-600">
                          {formatCurrency(tender.totalAmount)}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {percentage}%
                        </div>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full"
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Report Footer */}
          <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center text-sm text-gray-500 dark:text-gray-400">
              <div>
                Report generated: {formatDateTime(new Date().toISOString())}
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                Point of Sale Terminal Data Only
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
